import type {Model} from '@github-ui/marketplace-common'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {testIdProps} from '@github-ui/test-id-props'
import {Link} from '@primer/react'
import type {GettingStartedPayload} from '../../types'
import {InfoItem} from './InfoItem'

const tokenLabel = (n: number) => `${Math.round(n / 1000)}k`
const capitalize = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`

interface ModelDetailsProps {
  direction?: 'column' | 'row'
  model: Model
  multiTenantEnterprise?: boolean
}

export function ModelDetails({direction = 'column', model}: ModelDetailsProps) {
  const {docUrls} = useRoutePayload<GettingStartedPayload>()

  const rateLimitTier = model.rate_limit_tier
  const maxOutputTokens = model.max_output_tokens ? tokenLabel(model.max_output_tokens) : ''
  const maxInputTokens = model.max_input_tokens ? tokenLabel(model.max_input_tokens) : ''
  const inline = direction === 'row'
  const flexWrap = direction === 'row' ? 'flex-wrap' : 'flex-nowrap'

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div className={`d-flex flex-${direction} ${flexWrap} gap-2`} {...testIdProps('model-details')}>
      <InfoItem {...testIdProps('context')} isInline={inline} label="Context">
        {maxInputTokens ? <>{maxInputTokens} input</> : ''}
        {maxInputTokens && maxOutputTokens ? <> &middot; </> : ''}
        {maxOutputTokens ? <>{maxOutputTokens} output</> : ''}
      </InfoItem>
      <InfoItem {...testIdProps('training-date')} isInline={inline} label="Training date">
        {model.training_data_date || 'Undisclosed'}
      </InfoItem>

      {rateLimitTier && (
        <InfoItem {...testIdProps('rate-limit-tier')} isInline={inline} label="Free rate limit tier">
          <Link inline href={docUrls?.rateLimit} tabIndex={0}>
            {capitalize(rateLimitTier)}
          </Link>
        </InfoItem>
      )}

      <InfoItem isInline={inline} label="Pricing">
        <Link inline href="https://gh.io/github-models-pricing" tabIndex={0}>
          View pricing
        </Link>
      </InfoItem>

      <InfoItem isInline={inline} label="Provider support">
        <Link inline href={`https://ai.azure.com/github/support/${model.name}`} tabIndex={0}>
          Azure support site
        </Link>
      </InfoItem>
    </div>
  )
}
